import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { User, LoginCredentials, RegisterData, LoadingState } from '@types'
import { authApi } from '@api/auth'
import { ApiService, tokenStorage, apiErrorMessage } from '@api/index'

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  loadingState: LoadingState
}

const initialState: AuthState = {
  user: null,
  token: tokenStorage.getAccess(),
  isAuthenticated: false,
  isLoading: !!tokenStorage.getAccess(),
  error: null,
  loadingState: 'idle',
}

// Async thunks
export const signIn = createAsyncThunk(
  'auth/signIn',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const response = await authApi.signIn(credentials)
      const { user, access, refresh } = response.data.data

      // Persist both tokens: the refresh token is what keeps the session
      // alive past the access token's 24h lifetime.
      tokenStorage.set(access, refresh)

      return { user, token: access }
    } catch (error: any) {
      return rejectWithValue(apiErrorMessage(error, 'Sign in failed'))
    }
  }
)

export const googleSignIn = createAsyncThunk(
  'auth/googleSignIn',
  async (token: string, { rejectWithValue }) => {
    try {
      const response = await authApi.googleAuth(token)
      const { user, access, refresh } = response.data.data

      tokenStorage.set(access, refresh)

      return { user, token: access }
    } catch (error: any) {
      return rejectWithValue(apiErrorMessage(error, 'Google sign in failed'))
    }
  }
)

export const signUp = createAsyncThunk(
  'auth/signUp',
  async (userData: RegisterData, { rejectWithValue }) => {
    try {
      const response = await authApi.signUp(userData)
      const { user, access, refresh } = response.data.data

      tokenStorage.set(access, refresh)

      return { user, token: access }
    } catch (error: any) {
      if (error.response && error.response.data) {
        // Handle DRF validation errors (which are objects)
        const errors = error.response.data;
        const firstErrorField = Object.keys(errors)[0];
        const firstErrorMessage = errors[firstErrorField][0];
        return rejectWithValue(`${firstErrorField}: ${firstErrorMessage}` || 'Sign up failed');
      }
      return rejectWithValue('An unknown error occurred during sign up');
    }
  }
)

export const validateUser = createAsyncThunk(
  'auth/validateUser',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { auth: AuthState }
      if (!state.auth.token) {
        throw new Error('No token available')
      }

      const response = await authApi.validateUser()
      return response.data
    } catch (error: any) {
      // Clear invalid tokens
      tokenStorage.clear()
      return rejectWithValue(error.response?.data?.message || 'Token validation failed')
    }
  }
)

export const signOut = createAsyncThunk(
  'auth/signOut',
  async (_, { dispatch }) => {
    try {
      // Hand the refresh token back so the server can blacklist it, rather
      // than leaving a valid one usable for its full 7-day lifetime.
      await authApi.signOut(tokenStorage.getRefresh())
    } catch (error) {
      // Continue with sign out even if API call fails
      console.warn('Sign out API call failed:', error)
    } finally {
      tokenStorage.clear()

      // Reset all other slices
      dispatch({ type: 'locations/resetState' })
      dispatch({ type: 'devices/resetState' })
      dispatch({ type: 'alerts/resetState' })
      dispatch({ type: 'inspections/resetState' })
      dispatch({ type: 'checklists/resetState' })
      dispatch({ type: 'dashboards/resetState' })
    }
  }
)

export const updateUser = createAsyncThunk(
  'auth/updateUser',
  async (userData: Partial<User>, { rejectWithValue }) => {
    try {
      const response = await authApi.updateUser(userData)
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update user')
    }
  }
)

export const deleteUser = createAsyncThunk(
  'auth/deleteUser',
  async (_, { rejectWithValue }) => {
    try {
      await authApi.deleteUser()
      tokenStorage.clear()
      return true
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete user')
    }
  }
)

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (profileData: Partial<User>, { rejectWithValue }) => {
    try {
      const response = await ApiService.put('auth/profile/update/', profileData);
      // Backend wraps every response as { success, data, message }.
      return response.data?.data ?? response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update profile')
    }
  }
)

export const updateAvatar = createAsyncThunk(
  'auth/updateAvatar',
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const response = await ApiService.upload('auth/profile/avatar/', formData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update avatar')
    }
  }
)

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async (
    passwordData: { old_password: string; new_password: string; new_password_confirm: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await ApiService.post('auth/password/change/', passwordData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to change password')
    }
  }
)

export const updatePreferences = createAsyncThunk(
  'auth/updatePreferences',
  async (preferences: any, { rejectWithValue }) => {
    try {
      const response = await ApiService.post('auth/preferences/update/', preferences);
      // Backend wraps every response as { success, data, message }.
      return response.data?.data ?? response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update preferences')
    }
  }
)

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload
      tokenStorage.set(action.payload)
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Sign In
      .addCase(signIn.pending, (state) => {
        state.isLoading = true
        state.loadingState = 'pending'
        state.error = null
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.isLoading = false
        state.loadingState = 'succeeded'
        state.user = action.payload.user
        state.token = action.payload.token
        state.isAuthenticated = true
        state.error = null
      })
      .addCase(signIn.rejected, (state, action) => {
        state.isLoading = false
        state.loadingState = 'failed'
        state.error = action.payload as string
        state.isAuthenticated = false
        state.user = null
        state.token = null
      })

      // Google Sign In
      .addCase(googleSignIn.pending, (state) => {
        state.isLoading = true
        state.loadingState = 'pending'
        state.error = null
      })
      .addCase(googleSignIn.fulfilled, (state, action) => {
        state.isLoading = false
        state.loadingState = 'succeeded'
        state.user = action.payload.user
        state.token = action.payload.token
        state.isAuthenticated = true
        state.error = null
      })
      .addCase(googleSignIn.rejected, (state, action) => {
        state.isLoading = false
        state.loadingState = 'failed'
        state.error = action.payload as string
        state.isAuthenticated = false
        state.user = null
        state.token = null
      })

      // Sign Up
      .addCase(signUp.pending, (state) => {
        state.isLoading = true
        state.loadingState = 'pending'
        state.error = null
      })
      .addCase(signUp.fulfilled, (state, action) => {
        state.isLoading = false
        state.loadingState = 'succeeded'
        state.error = null
        // Auto-login after successful signup
        if (action.payload.user && action.payload.token) {
          state.user = action.payload.user
          state.token = action.payload.token
          state.isAuthenticated = true
        }
      })
      .addCase(signUp.rejected, (state, action) => {
        state.isLoading = false
        state.loadingState = 'failed'
        state.error = action.payload as string
      })

      // Validate User
      .addCase(validateUser.pending, (state) => {
        state.isLoading = true
        state.loadingState = 'pending'
      })
      .addCase(validateUser.fulfilled, (state, action) => {
        state.isLoading = false
        state.loadingState = 'succeeded'
        state.user = action.payload.data || action.payload
        state.isAuthenticated = true
        state.error = null
      })
      .addCase(validateUser.rejected, (state) => {
        state.isLoading = false
        state.loadingState = 'failed'
        state.user = null
        state.token = null
        state.isAuthenticated = false
      })

      // Sign Out
      .addCase(signOut.fulfilled, (state) => {
        state.user = null
        state.token = null
        state.isAuthenticated = false
        state.isLoading = false
        state.loadingState = 'idle'
        state.error = null
      })

      // Update Profile
      .addCase(updateProfile.fulfilled, (state, action) => {
        if (state.user) {
          state.user = { ...state.user, ...action.payload }
        }
      })

      // Update Avatar
      .addCase(updateAvatar.fulfilled, (state, action) => {
        if (state.user) {
          state.user = { ...state.user, avatar: action.payload.avatar }
        }
      })

      // Change Password
      .addCase(changePassword.fulfilled, () => {
        // Password changed successfully, no state update needed
      })

      // Update Preferences
      .addCase(updatePreferences.fulfilled, (state, action) => {
        if (state.user) {
          // The endpoint returns `language`, but the real User field is `locale`.
          const { language, ...rest } = action.payload || {}
          state.user = {
            ...state.user,
            ...rest,
            ...(language !== undefined ? { locale: language } : {}),
          }
        }
      })
  },
})

export const { clearError, setToken, resetState } = authSlice.actions

// Selectors
export const selectCurrentUser = (state: { auth: AuthState }): User | null => state.auth.user
export const selectIsAuthenticated = (state: { auth: AuthState }): boolean => state.auth.isAuthenticated
export const selectAuthLoading = (state: { auth: AuthState }): boolean => state.auth.isLoading
export const selectAuthError = (state: { auth: AuthState }): string | null => state.auth.error

export default authSlice.reducer
